import React, { createContext, useState, useContext, useCallback, useEffect, ReactNode } from 'react';
import { fetchCurrentUser } from '../services/api.ts';

interface User {
  id: string;
  username: string;
  email: string;
  role_name: string;
  permissions: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const token = localStorage.getItem('auth_token');
    return !!token;
  });
  
  const [user, setUser] = useState<User | null>(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr && userStr !== 'undefined' && userStr !== 'null') {
        return JSON.parse(userStr);
      }
      return null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  });

  // On mount, refresh user info from backend to pick up any role/permission changes
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    fetchCurrentUser()
      .then((freshUser) => {
        const updatedUser: User = {
          id: freshUser.id,
          username: freshUser.username,
          email: freshUser.email,
          role_name: freshUser.role_name,
          permissions: freshUser.permissions,
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      })
      .catch(() => {
        // Token invalid/expired — the 401 interceptor will handle logout
      });
  }, []);

  const login = (token: string, userData: User) => {
    try {
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      // Remove legacy session key if present
      localStorage.removeItem('session');
      setIsAuthenticated(true);
      setUser(userData);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('session');
    setIsAuthenticated(false);
    setUser(null);
  };

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  }, [user]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
